import { inject, Injectable, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import Collection from '@arcgis/core/core/Collection';
import EsriMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import {
  AvailableBasemaps,
  BasemapKey,
  JmkwApiService,
  store
} from '@jeppesen-foreflight/jmkw-core';
import {
  ErrorHandlerOptions,
  EsriErrorHandler,
  EsriRequestError,
  EsriViewType,
  ExtendedMap,
  JepBasemap,
  JepHitTestResult,
  JepLayer,
  JmkwApiInterface,
  JmkwEvent,
  JmkwThemeConfig,
  JmkwUnitConfig,
  JmkwUnitConfigKey,
  Plugin,
  PluginID,
  PluginMap,
  PurlySignedUrlInfo,
  ThemeInfo,
  ViewConfig,
  WidgetParams
} from '@jeppesen-foreflight/jmkw-types';
import { filter, Observable, shareReplay, take } from 'rxjs';
import type { GoToOptions3D, GoToOptions2D } from "@arcgis/core/views/types";

@Injectable({
  providedIn: 'root'
})
export class ToolkitApiService implements JmkwApiInterface {
  private _jmkwApi = inject(JmkwApiService);

  getPlugins(): PluginMap {
    return this._jmkwApi.getPlugins();
  }

  getPluginById(id: PluginID): Plugin | undefined {
    return this._jmkwApi.getPluginById(id);
  }

  findPlugin(partialPlugin: Partial<Plugin>): Plugin | null {
    return this._jmkwApi.findPlugin(partialPlugin);
  }

  getLayerById<LayerType extends JepLayer>(
    id: PluginID
  ): LayerType | undefined {
    return this._jmkwApi.getLayerById(id);
  }

  onLayerReady$<T extends JepLayer>(id: string): Observable<T> {
    return this._jmkwApi.onLayerReady$<T>(id);
  }

  getMapOfLayers(): ExtendedMap<JepLayer> {
    return this._jmkwApi.getMapOfLayers();
  }

  addWidget(id: PluginID, params: WidgetParams) {
    return this._jmkwApi.addWidget(id, params);
  }

  removeWidget(id: PluginID) {
    return this._jmkwApi.removeWidget(id);
  }

  getActiveWidgetMap(): ExtendedMap<WidgetParams> {
    return this._jmkwApi.getActiveWidgetMap();
  }

  private readonly _map$: Observable<EsriMap>;

  constructor() {
    this._map$ = toObservable(store.map);
  }

  get mapReady$() {
    return this._map$.pipe(
      filter((map) => !!map),
      take(1),
      shareReplay(1)
    );
  }

  getEvents(): Signal<JmkwEvent> {
    return this._jmkwApi.getEvents();
  }

  getEventStream(): Observable<JmkwEvent> {
    return this._jmkwApi.getEventStream();
  }

  dispatchEvent(jmkwEvent: JmkwEvent): void {
    this._jmkwApi.dispatchEvent(jmkwEvent);
  }

  getMap() {
    return this._jmkwApi.getMap();
  }

  getLayerCollection(): Collection<JepLayer> {
    return this._jmkwApi.getLayerCollection();
  }

  addLayer(layer: JepLayer): Observable<JepLayer> {
    return this._jmkwApi.addLayer(layer);
  }

  findLayer<LayerType extends JepLayer>(
    partialLayer: Partial<JepLayer>,
    layerType?: JepLayer['type']
  ): {
    layer: LayerType | null;
    path: { matchProperties: Partial<JepLayer>; index: number }[];
  } {
    return this._jmkwApi.findLayer(partialLayer, layerType);
  }

  replaceLayer(
    replacementLayer: JepLayer,
    matchProperties: (keyof Partial<JepLayer>)[] = ['id']
  ): {
    newLayer: JepLayer | null;
    replacedLayer: JepLayer | null;
  } {
    return this._jmkwApi.replaceLayer(replacementLayer, matchProperties);
  }

  removeLayerById<LayerType extends JepLayer>(id: string): LayerType | null {
    return this._jmkwApi.removeLayerById<LayerType>(id);
  }

  getMapView(): MapView {
    return this._jmkwApi.getMapView();
  }

  getSceneView(): SceneView {
    return this._jmkwApi.getSceneView();
  }

  getViews(): Record<EsriViewType, ViewConfig> {
    return this._jmkwApi.getViews();
  }

  toggleView(esriViewType: EsriViewType): boolean {
    return this._jmkwApi.toggleView(esriViewType);
  }

  /**
   * @deprecated
   * Move the map center to a given latitude/longitude
   * @param lat latitude
   * @param long longitude
   */
  public moveMapToLatLong(
    latitude: number,
    longitude: number
  ): Promise<unknown> {
    return this._jmkwApi.moveMapToLatLong(latitude, longitude);
  }

  public moveMapView(
    latitude: number | null | undefined,
    longitude: number | null | undefined,
    options?: GoToOptions2D
  ): Promise<unknown> {
    return this._jmkwApi.moveMapView(latitude, longitude, options);
  }

  public moveSceneView(
    latitude: number,
    longitude: number,
    options?: GoToOptions3D
  ): Promise<unknown> {
    return this._jmkwApi.moveSceneView(latitude, longitude, options);
  }

  // Calculate the latitude to shift the map given the zoom level
  // https://stackoverflow.com/questions/47498309/inverting-web-mercator-equations-to-convert-pixels-to-latitude-longitude
  getLatitudeShift(zoom: number) {
    return this._jmkwApi.getLatitudeShift(zoom);
  }

  zoomTo(level: number) {
    return this._jmkwApi.zoomTo(level);
  }

  /**
   * Sets the visibility of a layer or layers using a key-value pair of `ident` (id) and `visible`
   * @deprecated Implemented for compatibility with JMKW v2 LayerConfigurations
   * @see {@link PluginConfiguration}
   */
  setLayerVisibility(
    config:
      | { ident: string; visible: boolean }
      | { ident: string; visible: boolean }[]
  ) {
    return this._jmkwApi.setLayerVisibility(config);
  }

  mapViewHitTestStream(): Observable<JepHitTestResult> {
    return this._jmkwApi.mapViewHitTestStream();
  }

  setUnitConfig(newConfig: JmkwUnitConfig) {
    return this._jmkwApi.setUnitConfig(newConfig);
  }

  setUnit(unitType: JmkwUnitConfigKey, newUnit: string) {
    return this._jmkwApi.setUnit(unitType, newUnit);
  }

  getUnitConfig() {
    return this._jmkwApi.getUnitConfig();
  }

  /*******************************************************************************
   * Backgrounds and Themes (START)
   *******************************************************************************/

  /**
   *
   * @param themeName If called without a themeName being passed, the next theme available from the list of available themes will be used.
   */
  changeTheme(themeName?: string) {
    this._jmkwApi.changeTheme(themeName);
  }

  currentTheme(): Signal<ThemeInfo | undefined> {
    return this._jmkwApi.currentTheme();
  }

  currentTheme$(): Observable<ThemeInfo | undefined> {
    return this._jmkwApi.currentTheme$();
  }

  getThemeConfig(): JmkwThemeConfig {
    return this._jmkwApi.getThemeConfig();
  }

  getThemeOptions(): ThemeInfo[] | undefined {
    return this._jmkwApi.getThemeOptions();
  }

  onThemeChange$(): Observable<ThemeInfo | undefined> {
    return this._jmkwApi.onThemeChange$();
  }

  onBasemapChange$(): Observable<JepBasemap> {
    return this._jmkwApi.onBasemapChange$();
  }

  currentBasemap$(): Observable<JepBasemap> {
    return this._jmkwApi.currentBasemap$();
  }

  currentBasemap(): JepBasemap | null {
    return this._jmkwApi.currentBasemap();
  }

  registerBasemap(basemap: JepBasemap) {
    return this._jmkwApi.registerBasemap(basemap);
  }

  setBasemapById(identifier: BasemapKey): void {
    return this._jmkwApi.setBasemapById(identifier);
  }

  availableBasemaps(): AvailableBasemaps {
    return this._jmkwApi.availableBasemaps();
  }

  /*******************************************************************************
   * Backgrounds and Themes (END)
   *******************************************************************************/
  /**
   * Register an error handler for ESRI XHR requests matching a specific URL pattern
   * @param urlPattern The URL pattern to match against
   * @param handler A function that handles errors for matching URLs
   * @returns A function that can be called to remove this handler
   */
  registerEsriErrorHandler(
    urlPattern: string,
    options?: ErrorHandlerOptions,
    handler?: EsriErrorHandler
  ): Observable<EsriRequestError> {
    return this._jmkwApi.registerEsriErrorHandler(urlPattern, options, handler);
  }

  /**
   * Alias for removeEsriErrorHandler - unregisters error handlers for a specific URL pattern
   * @param urlPattern The URL pattern to remove handlers for
   */
  unregisterEsriErrorHandler(urlPattern: string): void {
    return this._jmkwApi.unregisterEsriErrorHandler(urlPattern);
  }

  /**
   * Remove all error handlers for a specific URL pattern
   * @param urlPattern The URL pattern to remove handlers for
   */
  removeEsriErrorHandler(urlPattern: string): void {
    return this._jmkwApi.removeEsriErrorHandler(urlPattern);
  }

  /**
   * Remove a specific error handler function for a URL pattern
   * @param urlPattern The URL pattern
   * @param handler The specific handler function to remove
   * @returns True if the handler was found and removed, false otherwise
   */
  removeSpecificEsriErrorHandler(
    urlPattern: string,
    handler: EsriErrorHandler
  ): boolean {
    return this._jmkwApi.removeSpecificEsriErrorHandler(urlPattern, handler);
  }

  /**
   * Get all registered ESRI error handlers
   * @returns A Map of URL patterns to arrays of handler functions
   */
  getEsriErrorHandlers(): Map<
    string,
    Array<{ handler: EsriErrorHandler; options: ErrorHandlerOptions }>
  > {
    return this._jmkwApi.getEsriErrorHandlers();
  }

  // Purly Service Integration
  isPurlyInitialized(): boolean {
    return this._jmkwApi.isPurlyInitialized();
  }

  isPurlyInitialized$(): Observable<boolean> {
    return this._jmkwApi.isPurlyInitialized$();
  }

  getPurlyBasemapUrls(): Record<string, PurlySignedUrlInfo> {
    return this._jmkwApi.getPurlyBasemapUrls();
  }

  getPurlyTileServiceUrls(): Record<string, PurlySignedUrlInfo> {
    return this._jmkwApi.getPurlyTileServiceUrls();
  }

  /**
   * Returns the current JMKW semantic version string sourced from the deployed
   * library, or null if unavailable or malformed.
   */
  getVersion(): string | null {
    return this._jmkwApi.getVersion();
  }
}
